import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDownload, FiRefreshCw, FiChevronDown, FiChevronUp, FiMail, FiPhone, FiFileText, FiHash } from 'react-icons/fi';
import { getInquiries } from '../../services/api';
import { format } from 'date-fns';

function InquiriesManager() {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    setIsLoading(true);
    try {
      const response = await getInquiries();
      setInquiries(response.data.inquiries);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load inquiries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="inquiries-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-dark-900">Inquiries</h1>
        <button
          onClick={loadInquiries}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <FiRefreshCw className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <p className="text-gray-600">Total Inquiries: <span className="font-semibold">{total}</span></p>
        </div>

        {inquiries.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No inquiries yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Ref ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Files</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {inquiries.map((inquiry) => (
                  <React.Fragment key={inquiry._id}>
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${expandedRow === inquiry._id ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleRow(inquiry._id)}
                    >
                      <td className="px-4 py-4 text-gray-500">
                        {expandedRow === inquiry._id ? <FiChevronUp /> : <FiChevronDown />}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700">
                          {inquiry.reference_id || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(inquiry.submitted_at), 'MMM dd, yyyy')}
                        <div className="text-xs text-gray-400">
                          {format(new Date(inquiry.submitted_at), 'HH:mm')}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-dark-900">
                        {inquiry.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {inquiry.organization}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                          {inquiry.inquiry_type}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <FiMail className="mr-1 text-gray-400" size={12} />
                          <a href={`mailto:${inquiry.email}`} className="hover:text-primary-600" onClick={(e) => e.stopPropagation()}>
                            {inquiry.email}
                          </a>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <FiPhone className="mr-1 text-gray-400" size={10} />
                          {inquiry.phone}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {inquiry.files && inquiry.files.length > 0 ? (
                          <span className="text-primary-600 font-medium">
                            {inquiry.files.length} file(s)
                          </span>
                        ) : inquiry.file ? (
                          <span className="text-primary-600 font-medium">1 file</span>
                        ) : (
                          <span className="text-gray-400">No files</span>
                        )}
                      </td>
                    </tr>
                    
                    {/* Expanded Row - Requirement Summary and Files */}
                    {expandedRow === inquiry._id && (
                      <tr className="bg-blue-50">
                        <td colSpan="8" className="px-4 py-4">
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Requirement Summary */}
                            <div>
                              <div className="flex items-center mb-2">
                                <FiFileText className="mr-2 text-primary-600" />
                                <h4 className="font-semibold text-gray-800">Requirement Summary</h4>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
                                {inquiry.requirement_summary || 'No requirement summary provided'}
                              </div>
                            </div>
                            
                            {/* Files Section */}
                            <div>
                              <div className="flex items-center mb-2">
                                <FiDownload className="mr-2 text-primary-600" />
                                <h4 className="font-semibold text-gray-800">Attached Files</h4>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                {inquiry.files && inquiry.files.length > 0 ? (
                                  <ul className="space-y-2">
                                    {inquiry.files.map((file, idx) => (
                                      <li key={idx} className="flex items-center justify-between">
                                        <span className="text-sm text-gray-700">{file.filename}</span>
                                        <a
                                          href={file.download_url}
                                          className="text-primary-600 hover:text-primary-700 flex items-center text-sm"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <FiDownload className="mr-1" size={14} />
                                          Download
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                ) : inquiry.file ? (
                                  <a
                                    href={inquiry.file.download_url}
                                    className="text-primary-600 hover:text-primary-700 flex items-center text-sm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FiDownload className="mr-1" size={14} />
                                    {inquiry.file.filename}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 text-sm">No files attached</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Additional Info */}
                          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center text-xs text-gray-500">
                            <FiHash className="mr-1" />
                            <span>Reference: {inquiry.reference_id || 'N/A'}</span>
                            <span className="mx-3">•</span>
                            <span>Status: {inquiry.status || 'new'}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default InquiriesManager;